import { useEffect, useState, useRef } from "react";
import { Card, SectionHeader } from "../components/Card";
import { Button } from "../components/Button";
import { Input } from "../components/Input";
import { useAuth } from "../lib/auth.jsx";
import { bookingDisplayCode } from "../lib/bookingCode.js";
import { BookingDetailModal, DesignDetailModal } from "../components/DesignDetailModal";
import { CURRENCY_SYMBOLS } from "./AdminDashboard";
import { NailPlanViewer } from "../components/NailPlanViewer.jsx";
import { buildNailPlan, planCompletion } from "../lib/nailPlan.js";
import { bookingDesign, designImageUrl } from "../lib/designMedia.js";

function fmtCurrency(cents, currencyCode = "USD") {
  const code = String(currencyCode || "USD").toUpperCase();
  const amount = (Number(cents || 0) / 100).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${CURRENCY_SYMBOLS[code] || code}${amount}`;
}

export function TechDashboardV2() {
  const { profile, authedFetch } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [inbox, setInbox] = useState([]);   // assigned-but-not-yet-booked requests
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [currencyCode, setCurrencyCode] = useState("USD");
  const [detailBooking, setDetailBooking] = useState(null);
  const [detailRequest, setDetailRequest] = useState(null);
  const [workBooking, setWorkBooking] = useState(null);
  const [photoUploadBooking, setPhotoUploadBooking] = useState(null); // booking being photo-uploaded
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoCaption, setPhotoCaption] = useState("");
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoUploadSuccess, setPhotoUploadSuccess] = useState(false);
  const photoInputRef = useRef(null);

  // Quick "add slot" form
  const [slotDate, setSlotDate] = useState("");
  const [slotTime, setSlotTime] = useState("");

  const [earnings, setEarnings] = useState(null);
  const [connect, setConnect]   = useState(null);
  const [me, setMe]             = useState(null);

  // Recurring weekly schedule (templates)
  const [templates, setTemplates] = useState([]);
  const [tplWeekday, setTplWeekday] = useState(1);   // default: Monday
  const [tplStart, setTplStart]     = useState("09:00");
  const [tplEnd, setTplEnd]         = useState("17:00");
  const [tplMinutes, setTplMinutes] = useState(60);

  async function refresh() {
    try {
      const [b, inb, e, c, m, t] = await Promise.all([
        authedFetch("/api/bookings/assigned"),
        authedFetch("/api/bookings/inbox").catch(() => ({ requests: [] })),
        authedFetch("/api/tech/earnings").catch(() => null),
        authedFetch("/api/connect/status").catch(() => null),
        authedFetch("/api/tech/me").catch(() => null),
        authedFetch("/api/tech/templates").catch(() => ({ templates: [] })),
      ]);
      setBookings(b.bookings);
      setInbox(inb?.requests || []);
      if (e) {
        setEarnings(e);
        if (e.currency) setCurrencyCode(String(e.currency).toUpperCase());
      }
      if (c) setConnect(c);
      if (m?.technician) setMe(m.technician);
      setTemplates(t?.templates || []);
      try {
        const cc = await authedFetch("/api/settings/currency");
        if (cc?.currency) setCurrencyCode(cc.currency);
      } catch (_) {}
    } catch (e) { setErr(e.message); }
    setLoading(false);
  }

  async function addTemplate(e) {
    e.preventDefault();
    if (!tplStart || !tplEnd) return;
    if (tplEnd <= tplStart) {
      setErr("End time must be after start time."); return;
    }
    try {
      await authedFetch("/api/tech/templates", {
        method: "POST",
        body: JSON.stringify({
          weekday: Number(tplWeekday),
          start_time: tplStart,
          end_time: tplEnd,
          slot_minutes: Number(tplMinutes) || 60,
        }),
      });
      // Keep the form values so adding several days in a row is quick.
      refresh();
    } catch (e) { setErr(e.message); }
  }

  async function deleteTemplate(id) {
    try {
      await authedFetch(`/api/tech/templates/${id}`, { method: "DELETE" });
      refresh();
    } catch (e) { setErr(e.message); }
  }

  async function updateMe(patch) {
    try {
      const out = await authedFetch("/api/tech/me", { method: "PATCH", body: JSON.stringify(patch) });
      setMe(out.technician);
    } catch (e) { setErr(e.message); }
  }

  async function startConnect() {
    try {
      const out = await authedFetch("/api/connect/onboard", { method: "POST" });
      window.location.href = out.url;
    } catch (e) { setErr(e.message); }
  }

  async function materialiseTemplates() {
    if (templates.length === 0) {
      alert("Add a recurring weekly schedule first — pick a weekday and start/end time below, then tap “materialise next 4 weeks”.");
      return;
    }
    try {
      const out = await authedFetch("/api/tech/templates/materialise", { method: "POST", body: JSON.stringify({ weeks: 4 }) });
      if (!out.created) {
        alert(
          "No new slots were created. This usually means the next 4 weeks are already filled, " +
          "or every template window has already passed for today. Try again tomorrow or add a new template.",
        );
      } else {
        alert(`Created ${out.created} availability slots from your templates.`);
      }
    } catch (e) { setErr(e.message); }
  }

  const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  useEffect(() => { refresh(); }, []);

  async function markStatus(b, status) {
    try {
      await authedFetch(`/api/bookings/${b.id}`, {
        method: "PATCH",
        body: JSON.stringify({ status }),
      });
      refresh();
    } catch (e) { setErr(e.message); }
  }

  async function updateNailStatus(booking, nailKey, status) {
    try {
      const out = await authedFetch(`/api/bookings/${booking.id}/work-progress`, {
        method: "PATCH",
        body: JSON.stringify({ nailKey, status }),
      });
      setBookings((prev) => prev.map((b) => b.id === booking.id ? out.booking : b));
      setWorkBooking(out.booking);
    } catch (e) { setErr(e.message); }
  }

  async function markFinalPaymentPaid(b) {
    try {
      await authedFetch(`/api/invoices/booking/${b.id}/mark-paid`, { method: "POST" });
      refresh();
    } catch (e) { setErr(e.message); }
  }

  async function clearInboxItem(requestId) {
    const previous = inbox;
    setInbox((prev) => prev.filter((r) => r.id !== requestId));
    try {
      await authedFetch(`/api/bookings/inbox/${requestId}/clear`, { method: "POST" });
    } catch (e) {
      setInbox(previous);
      setErr(e.message);
    }
  }

  async function clearInboxAll() {
    const previous = inbox;
    setInbox([]);
    try {
      await authedFetch("/api/bookings/inbox/clear-all", { method: "POST" });
    } catch (e) {
      setInbox(previous);
      setErr(e.message);
    }
  }

  function openPhotoUpload(booking) {
    setPhotoUploadBooking(booking);
    setPhotoFile(null);
    setPhotoPreview(null);
    setPhotoCaption("");
    setPhotoUploadSuccess(false);
  }

  function closePhotoUpload() {
    setPhotoUploadBooking(null);
    setPhotoFile(null);
    setPhotoPreview(null);
    setPhotoCaption("");
    setPhotoUploadSuccess(false);
    if (photoInputRef.current) photoInputRef.current.value = "";
  }

  function handlePhotoFileChange(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    setPhotoFile(f);
    setPhotoPreview(URL.createObjectURL(f));
    setPhotoUploadSuccess(false);
  }

  async function submitWorkPhoto() {
    if (!photoFile || !photoUploadBooking) return;
    setPhotoUploading(true);
    try {
      const form = new FormData();
      form.append("image", photoFile);
      form.append("booking_id", photoUploadBooking.id);
      if (photoCaption.trim()) form.append("description", photoCaption.trim());

      await authedFetch("/api/gallery/upload", { method: "POST", body: form });
      setPhotoUploadSuccess(true);
      setPhotoFile(null);
      setPhotoPreview(null);
      setPhotoCaption("");
      if (photoInputRef.current) photoInputRef.current.value = "";
    } catch (e) {
      setErr(e.message);
    }
    setPhotoUploading(false);
  }

  async function addSlot(e) {
    e.preventDefault();
    if (!slotDate || !slotTime) return;
    const start = new Date(`${slotDate}T${slotTime}`);
    const end = new Date(start.getTime() + 60 * 60_000); // 1 hour
    try {
      await authedFetch("/api/availability/bulk", {
        method: "POST",
        body: JSON.stringify({
          technicianId: profile.id,
          slots: [{ starts_at: start.toISOString(), ends_at: end.toISOString() }],
        }),
      });
      setSlotDate(""); setSlotTime("");
    } catch (e) { setErr(e.message); }
  }

  return (
    <div style={{ minHeight: "100vh", background: "var(--bg)", paddingBottom: 80 }}>
      <div style={{ maxWidth: 920, margin: "0 auto", padding: "30px 16px" }}>
        <SectionHeader emoji="👩‍🎨" title="Technician Dashboard"
          subtitle={`Hi ${profile?.full_name || "tech"}. Here are your assigned bookings.`} />

        {earnings && (
          <Card style={{ marginBottom: 18 }}>
            <h3 style={{ fontFamily: "var(--font-display)", color: "var(--text)", marginBottom: 12 }}>Earnings (last 90 days)</h3>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 12 }}>
              <Stat label="Gross"      value={fmtCurrency(earnings.grossCents, currencyCode)} />
              <Stat label="Tips"       value={fmtCurrency(earnings.tipsCents, currencyCode)} />
              <Stat label="Discounts"  value={`-${fmtCurrency(earnings.discountsCents, currencyCode)}`} />
              <Stat label="Net"        value={fmtCurrency(earnings.netCents, currencyCode)} accent />
              <Stat label="Bookings"   value={earnings.bookingCount} />
              <Stat label="No-shows"   value={earnings.noShowCount} />
            </div>
          </Card>
        )}

        {me && (
          <Card style={{ marginBottom: 18 }}>
            <h3 style={{ fontFamily: "var(--font-display)", color: "var(--text)", marginBottom: 12 }}>Preferences</h3>
            <div style={{ display: "grid", gap: 8 }}>
              <label style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: "1px solid var(--border)" }}>
                <span style={{ fontSize: 14, color: "var(--text)" }}>Auto-confirm new bookings</span>
                <input type="checkbox" checked={!!me.auto_confirm} onChange={(e) => updateMe({ auto_confirm: e.target.checked })} />
              </label>
              <label style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: "1px solid var(--border)" }}>
                <span style={{ fontSize: 14, color: "var(--text)" }}>Show me on the public technician list</span>
                <input type="checkbox" checked={!!me.portfolio_visible} onChange={(e) => updateMe({ portfolio_visible: e.target.checked })} />
              </label>
            </div>
          </Card>
        )}

        <Card style={{ marginBottom: 18 }}>
          <h3 style={{ fontFamily: "var(--font-display)", color: "var(--text)", marginBottom: 12 }}>Stripe payouts (Connect)</h3>
          {connect?.connected ? (
            <div style={{ fontSize: 13, color: connect.payoutsEnabled ? "var(--text-confirm)" : "var(--text-muted)" }}>
              {connect.payoutsEnabled ? "✓ Payouts enabled — money flows to your bank." : "Almost there — Stripe still needs more info."}
              {!connect.payoutsEnabled && (
                <div style={{ marginTop: 10 }}>
                  <Button size="sm" variant="primary" label="Continue Stripe setup" onClick={startConnect} />
                </div>
              )}
            </div>
          ) : (
            <>
              <p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 10 }}>
                Connect your bank to receive payouts directly from your bookings.
              </p>
              <Button label="Connect with Stripe" icon="🏦" variant="primary" onClick={startConnect} />
            </>
          )}
        </Card>

        <Card style={{ marginBottom: 24 }}>
          <h3 style={{ fontFamily: "var(--font-display)", color: "var(--text)", marginBottom: 12 }}>
            Add availability
          </h3>
          <form onSubmit={addSlot} style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
            <div style={{ flex: 1, minWidth: 160 }}>
              <Input label="Date" type="date" required value={slotDate} onChange={(e) => setSlotDate(e.target.value)} />
            </div>
            <div style={{ flex: 1, minWidth: 140 }}>
              <Input label="Start time" type="time" required value={slotTime} onChange={(e) => setSlotTime(e.target.value)} />
            </div>
            <Button type="submit" label="Add 1-hour slot" icon="+" variant="primary" />
          </form>
          <div style={{ marginTop: 12, fontSize: 13, color: "var(--text-muted)" }}>
            Or set a recurring weekly schedule below and tap{" "}
            <button type="button" onClick={materialiseTemplates} style={{ background: "none", border: "none", color: "var(--accent)", fontWeight: 600, cursor: "pointer", textDecoration: "underline" }}>
              materialise next 4 weeks
            </button>.
          </div>
        </Card>

        <Card style={{ marginBottom: 24 }}>
          <h3 style={{ fontFamily: "var(--font-display)", color: "var(--text)", marginBottom: 6 }}>
            Recurring weekly schedule
          </h3>
          <p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 12 }}>
            Define the days and hours you normally work. Tap “materialise next 4 weeks”
            above to expand them into bookable slots in one click.
          </p>

          {templates.length > 0 && (
            <div style={{ display: "grid", gap: 8, marginBottom: 14 }}>
              {templates
                .slice()
                .sort((a, b) => a.weekday - b.weekday || a.start_time.localeCompare(b.start_time))
                .map((t) => (
                  <div key={t.id} style={{
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                    padding: "10px 12px", borderRadius: 12, border: "1px solid var(--border)",
                    background: "var(--bg-elevated)",
                  }}>
                    <div style={{ fontSize: 13, color: "var(--text)" }}>
                      <b>{WEEKDAYS[t.weekday]}</b>
                      {" · "}
                      {t.start_time?.slice(0, 5)} – {t.end_time?.slice(0, 5)}
                      {" · "}
                      <span style={{ color: "var(--text-muted)" }}>{t.slot_minutes} min slots</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => deleteTemplate(t.id)}
                      style={{
                        background: "none", border: "1px solid var(--border)",
                        color: "var(--text-muted)", borderRadius: 8, padding: "4px 10px",
                        fontSize: 12, fontWeight: 600, cursor: "pointer",
                      }}
                      aria-label={`Remove ${WEEKDAYS[t.weekday]} ${t.start_time}-${t.end_time} template`}
                    >
                      Remove
                    </button>
                  </div>
                ))}
            </div>
          )}

          <form onSubmit={addTemplate} style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
            <div style={{ minWidth: 130 }}>
              <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "var(--text-muted)", marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.6 }}>
                Day
              </label>
              <select
                value={tplWeekday}
                onChange={(e) => setTplWeekday(e.target.value)}
                style={{
                  width: "100%", padding: "10px 12px", borderRadius: 12,
                  border: "1px solid var(--border)", background: "var(--bg-elevated)",
                  color: "var(--text)", fontSize: 14,
                }}
              >
                {WEEKDAYS.map((d, i) => <option key={i} value={i}>{d}</option>)}
              </select>
            </div>
            <div style={{ flex: 1, minWidth: 130 }}>
              <Input label="Start" type="time" required value={tplStart} onChange={(e) => setTplStart(e.target.value)} />
            </div>
            <div style={{ flex: 1, minWidth: 130 }}>
              <Input label="End" type="time" required value={tplEnd} onChange={(e) => setTplEnd(e.target.value)} />
            </div>
            <div style={{ minWidth: 110 }}>
              <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "var(--text-muted)", marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.6 }}>
                Slot length
              </label>
              <select
                value={tplMinutes}
                onChange={(e) => setTplMinutes(e.target.value)}
                style={{
                  width: "100%", padding: "10px 12px", borderRadius: 12,
                  border: "1px solid var(--border)", background: "var(--bg-elevated)",
                  color: "var(--text)", fontSize: 14,
                }}
              >
                <option value={30}>30 min</option>
                <option value={45}>45 min</option>
                <option value={60}>60 min</option>
                <option value={90}>90 min</option>
                <option value={120}>120 min</option>
              </select>
            </div>
            <Button type="submit" label="Add to schedule" icon="+" variant="primary" />
          </form>
        </Card>

        {inbox.length > 0 && (
          <Card style={{
            marginBottom: 18,
            border: "1px solid var(--border-strong)",
            background: "linear-gradient(135deg, rgba(196,135,154,0.08), rgba(232,180,184,0.04))",
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", marginBottom: 6 }}>
              <h3 style={{ fontFamily: "var(--font-display)", color: "var(--text)", margin: 0 }}>
                Heading your way ({inbox.length})
              </h3>
              <Button size="sm" label="Clear all" variant="outline" onClick={clearInboxAll} />
            </div>
            <p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 12 }}>
              Customers an admin has routed to you. They'll appear in <b>Bookings</b> below
              once they pick a slot.
            </p>
            <div style={{ display: "grid", gap: 10 }}>
              {inbox.map((r) => {
                const d = r.final_design || {};
                const imageUrl = designImageUrl(d);
                return (
                  <div key={r.id} style={{
                    display: "flex", gap: 12, padding: 12, borderRadius: 12,
                    background: "var(--bg-elevated)", border: "1px solid var(--border)",
                    alignItems: "center",
                  }}>
                    {imageUrl && <img src={imageUrl} alt="" style={{ width: 56, height: 56, borderRadius: 10, objectFit: "cover" }} />}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, color: "var(--text)", fontSize: 14 }}>
                        {r.customer?.full_name || r.customer?.email || "Customer"}
                      </div>
                      <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
                        {d.name || r.category} · awaiting slot pick
                      </div>
                      {(d.tagline || r.notes) && (
                        <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 3 }}>
                          {d.tagline || r.notes}
                        </div>
                      )}
                    </div>
                    <span style={{
                      fontSize: 10, fontWeight: 700, padding: "3px 10px", borderRadius: 999,
                      background: "rgba(201,168,76,0.18)", color: "var(--text-gold)",
                      letterSpacing: 0.6,
                    }}>
                      ASSIGNED
                    </span>
                    <Button size="sm" label="Clear" variant="outline" onClick={() => clearInboxItem(r.id)} />
                    <Button size="sm" label="View plan" variant="primary" onClick={() => setDetailRequest(r)} />
                  </div>
                );
              })}
            </div>
          </Card>
        )}

        <h3 style={{ fontFamily: "var(--font-display)", color: "var(--text)", margin: "20px 0 12px" }}>
          Bookings ({bookings.length})
        </h3>
        {loading ? <Card><div style={{ textAlign: "center" }}>Loading…</div></Card> : (
          bookings.length === 0 ? (
            <Card><div style={{ textAlign: "center", color: "var(--text-muted)" }}>Nothing assigned yet.</div></Card>
          ) : (
            <div style={{ display: "grid", gap: 14 }}>
              {bookings.map((b) => {
                const d = bookingDesign(b);
                const imageUrl = designImageUrl(d);
                const invoice = b.invoice || b.invoices?.[0] || null;
                const invoiceStatus = String(invoice?.status || "").toLowerCase();
                const finalPaymentClosed = ["paid", "void", "voided", "failed", "refunded"].includes(invoiceStatus);
                return (
                  <article key={b.id} style={{
                    background: "var(--bg-elevated)", borderRadius: 16,
                    border: "1px solid var(--border)", overflow: "hidden",
                  }}>
                    <div style={{ display: "flex", gap: 14, padding: 16, alignItems: "flex-start", flexWrap: "wrap" }}>
                      {imageUrl && <img src={imageUrl} alt="" style={{ width: "clamp(72px, 20vw, 120px)", height: "clamp(72px, 20vw, 120px)", borderRadius: 12, objectFit: "cover", flexShrink: 0 }} />}
                      <div style={{ flex: 1 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "flex-start", flexWrap: "wrap" }}>
                          <div>
                            <div style={{ fontFamily: "var(--font-display)", fontSize: 17, fontWeight: 700, color: "var(--text)" }}>
                              {b.customer?.full_name || b.customer?.email}
                            </div>
                            <div style={{ fontSize: 12, color: "var(--text-accent)", fontWeight: 800, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>
                              Booking #{bookingDisplayCode(b.id)}
                            </div>
                            <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
                              Appointment: {b.slot ? new Date(b.slot.starts_at).toLocaleString() : "Slot pending"}
                            </div>
                            <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
                              Booked: {b.created_at ? new Date(b.created_at).toLocaleString() : "Unknown"}
                              {["completed", "complete"].includes(b.status) && b.updated_at ? ` · Completed: ${new Date(b.updated_at).toLocaleString()}` : ""}
                            </div>
                          </div>
                          <span style={{ fontSize: 11, fontWeight: 700, padding: "4px 12px", borderRadius: 999,
                            background: "rgba(196,135,154,0.16)", color: "var(--text-accent)" }}>
                            {formatStatus(b.status)}
                          </span>
                          {invoice && (
                            <span style={{ fontSize: 11, fontWeight: 800, padding: "4px 12px", borderRadius: 999,
                              background: invoiceStatus === "paid" ? "rgba(50,160,80,0.16)" : "var(--bg-soft)",
                              color: invoiceStatus === "paid" ? "var(--text-confirm)" : "var(--text-muted)",
                              border: "1px solid var(--border)" }}>
                              {invoiceStatus === "paid" ? "Paid" : `Invoice ${formatStatus(invoice.status)}`}
                            </span>
                          )}
                        </div>
                        <div style={{ marginTop: 10, color: "var(--text-soft)", fontSize: 13 }}>
                          <b>Design:</b> {d.name || "—"} · {d.tagline || ""}
                        </div>
                        {(d.products || []).length > 0 && (
                          <div style={{ marginTop: 10, display: "flex", flexWrap: "wrap", gap: 6 }}>
                            {d.products.map((p, i) => (
                              <span key={i} style={{ fontSize: 11, padding: "4px 10px", borderRadius: 999,
                                background: "rgba(196,135,154,0.14)", color: "var(--text-accent)", border: "1px solid var(--border-strong)", fontWeight: 600 }}>
                                ✓ {p}
                              </span>
                            ))}
                          </div>
                        )}
                        <div style={{ marginTop: 14, display: "flex", gap: 8, flexWrap: "wrap" }}>
                          <Button size="sm" label="View Details" variant="outline" onClick={() => setDetailBooking(b)} />
                          {b.status === "pending" && (
                            <Button size="sm" label="Confirm" variant="primary" onClick={() => markStatus(b, "confirmed")} />
                          )}
                          {b.status === "confirmed" && (
                            <Button size="sm" label="Start Work" icon="▶" variant="primary" onClick={() => markStatus(b, "in_progress")} />
                          )}
                          {b.status === "in_progress" && (
                            <Button size="sm" label="Work Mode" variant="primary" onClick={() => setWorkBooking(b)} />
                          )}
                          {b.status === "in_progress" && (
                            <Button size="sm" label="Mark Completed" variant="gold" onClick={() => markStatus(b, "completed")} />
                          )}
                          {b.status === "completed" && !finalPaymentClosed && (
                            <Button size="sm" label="Mark Final Payment Paid" variant="outline" onClick={() => markFinalPaymentPaid(b)} />
                          )}
                          {(b.status === "in_progress" || b.status === "completed") && (
                            <Button size="sm" label="📸 Upload Work Photo" variant="outline" onClick={() => openPhotoUpload(b)} />
                          )}
                          {b.status !== "cancelled" && b.status !== "completed" && (
                            <Button size="sm" label="Cancel" variant="danger" onClick={() => markStatus(b, "cancelled")} />
                          )}
                        </div>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )
        )}
        {workBooking && (
          <WorkModeModal
            booking={workBooking}
            onClose={() => setWorkBooking(null)}
            onSetStatus={(nailKey, status) => updateNailStatus(workBooking, nailKey, status)}
            onComplete={() => markStatus(workBooking, "completed")}
          />
        )}
        {detailRequest && (
          <DesignDetailModal req={detailRequest} onClose={() => setDetailRequest(null)} showCustomer />
        )}
        {detailBooking && (
          <BookingDetailModal
            booking={detailBooking}
            title="Booking details"
            onClose={() => setDetailBooking(null)}
            onMarkPaid={() => markFinalPaymentPaid(detailBooking)}
            onCancel={detailBooking.status !== "cancelled" && detailBooking.status !== "completed" ? () => markStatus(detailBooking, "cancelled") : null}
          />
        )}

        {/* Work Photo Upload Modal */}
        {photoUploadBooking && (
          <div
            onClick={closePhotoUpload}
            style={{
              position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)",
              display: "flex", alignItems: "center", justifyContent: "center",
              padding: 16, zIndex: 1100, backdropFilter: "blur(4px)"
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: "var(--bg-elevated)", borderRadius: 20, padding: 28,
                maxWidth: 520, width: "100%", maxHeight: "90vh", overflowY: "auto",
                boxShadow: "0 20px 60px rgba(0,0,0,0.3)"
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
                <h3 style={{ fontFamily: "var(--font-display)", fontSize: 22, fontWeight: 700, color: "var(--text)", margin: 0 }}>
                  Upload Work Photo
                </h3>
                <button onClick={closePhotoUpload} style={{ background: "none", border: "none", fontSize: 24, cursor: "pointer", color: "var(--text-muted)" }}>×</button>
              </div>

              <p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 18 }}>
                Upload a photo of your finished work on{" "}
                <strong>{photoUploadBooking.customer?.full_name || "this customer"}</strong>.
                It will appear in the gallery and in this booking's history.
              </p>

              {photoUploadSuccess ? (
                <div style={{
                  padding: 24, textAlign: "center", background: "rgba(50,160,80,0.1)",
                  borderRadius: 12, border: "1px solid rgba(50,160,80,0.3)"
                }}>
                  <div style={{ fontSize: 36, marginBottom: 10 }}>✅</div>
                  <div style={{ fontWeight: 700, color: "var(--text)", marginBottom: 6 }}>Photo uploaded!</div>
                  <div style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 16 }}>
                    It's now visible in the gallery and linked to this booking.
                  </div>
                  <Button label="Upload another" variant="outline" onClick={() => { setPhotoUploadSuccess(false); }} />
                  <span style={{ margin: "0 8px" }} />
                  <Button label="Done" variant="primary" onClick={closePhotoUpload} />
                </div>
              ) : (
                <>
                  {/* File picker */}
                  <div
                    onClick={() => photoInputRef.current?.click()}
                    style={{
                      border: `2px dashed ${photoPreview ? "var(--mauve)" : "var(--border-strong)"}`,
                      borderRadius: 14, cursor: "pointer",
                      background: photoPreview ? "transparent" : "var(--bg-soft)",
                      overflow: "hidden", marginBottom: 14,
                      padding: photoPreview ? 0 : 32, textAlign: "center"
                    }}
                  >
                    {photoPreview ? (
                      <img src={photoPreview} alt="Preview" style={{ width: "100%", maxHeight: 280, objectFit: "cover", display: "block" }} />
                    ) : (
                      <>
                        <div style={{ fontSize: 36, marginBottom: 8 }}>📷</div>
                        <div style={{ fontWeight: 700, color: "var(--text)", marginBottom: 4 }}>Tap to choose photo</div>
                        <div style={{ fontSize: 12, color: "var(--text-muted)" }}>JPG, PNG, WebP · max 10 MB</div>
                      </>
                    )}
                  </div>
                  <input ref={photoInputRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handlePhotoFileChange} />

                  {photoFile && (
                    <div style={{ marginBottom: 14 }}>
                      <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--text-muted)", marginBottom: 6 }}>
                        Caption (optional)
                      </label>
                      <input
                        value={photoCaption}
                        onChange={(e) => setPhotoCaption(e.target.value)}
                        placeholder="e.g. Ombre gel with chrome finish"
                        style={{
                          width: "100%", padding: "10px 14px", borderRadius: 8,
                          border: "1.5px solid var(--border-strong)",
                          background: "var(--bg)", color: "var(--text)", fontSize: 14
                        }}
                      />
                    </div>
                  )}

                  <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
                    <Button label="Cancel" variant="outline" onClick={closePhotoUpload} />
                    <Button
                      label={photoUploading ? "Uploading…" : "Upload Photo"}
                      variant="primary"
                      onClick={submitWorkPhoto}
                      disabled={!photoFile || photoUploading}
                      loading={photoUploading}
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {err && <Card style={{ marginTop: 14 }}><div role="alert" style={{ color: "#C02020" }}>{err}</div></Card>}
      </div>
    </div>
  );
}

function formatStatus(raw = "") {
  const value = String(raw).toLowerCase();
  if (value === "in_progress") return "In Progress";
  if (value === "no_show" || value === "no-show") return "No Show";
  return String(raw).replace(/[_-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function WorkModeModal({ booking, onClose, onSetStatus, onComplete }) {
  const generated = Array.isArray(booking.request?.generated) ? booking.request.generated : [];
  const design = booking.request?.final_design || generated[0] || {};
  const plan = buildNailPlan(design);
  const completion = planCompletion(plan, booking.work_progress || {});

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.68)",
        zIndex: 1200,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(980px, 100%)",
          maxHeight: "90vh",
          overflowY: "auto",
          background: "var(--bg-elevated)",
          border: "1px solid var(--border)",
          borderRadius: 18,
          padding: 20,
          boxShadow: "0 20px 70px rgba(0,0,0,0.42)",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start", marginBottom: 14 }}>
          <div>
            <div style={{ color: "var(--text-accent)", fontWeight: 900, fontSize: 12, textTransform: "uppercase", letterSpacing: 0.8 }}>
              Interactive work mode
            </div>
            <h3 style={{ margin: "4px 0 0", color: "var(--text)", fontFamily: "var(--font-display)", fontSize: 26 }}>
              {design.name || "Nail set"}
            </h3>
            <p style={{ margin: "5px 0 0", color: "var(--text-muted)", fontSize: 13 }}>
              Complete each hand and finger in sequence. Timer data is saved for future duration estimates.
            </p>
          </div>
          <button onClick={onClose} aria-label="Close" style={{ background: "none", border: "none", color: "var(--text-muted)", fontSize: 24, cursor: "pointer" }}>×</button>
        </div>

        <NailPlanViewer
          design={design}
          workProgress={booking.work_progress || {}}
          mode="work"
          onSetStatus={onSetStatus}
        />

        <div style={{
          marginTop: 16,
          display: "flex",
          justifyContent: "space-between",
          gap: 10,
          alignItems: "center",
          flexWrap: "wrap",
          borderTop: "1px solid var(--border)",
          paddingTop: 14,
        }}>
          <div style={{ color: "var(--text-muted)", fontSize: 13 }}>
            {completion.complete
              ? "All nails are complete. You can close the booking."
              : `${completion.total - completion.completed} nail${completion.total - completion.completed === 1 ? "" : "s"} remaining before completion.`}
          </div>
          <Button
            label="Complete booking"
            variant="gold"
            disabled={!completion.complete}
            onClick={onComplete}
          />
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div style={{
      background: accent ? "linear-gradient(135deg, var(--mauve), var(--mauve-deep))" : "var(--bg-soft)",
      color: accent ? "#fff" : "var(--text)",
      borderRadius: 12, padding: "12px 14px",
    }}>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.2, textTransform: "uppercase", opacity: 0.7 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, fontFamily: "var(--font-display)", marginTop: 4 }}>{value}</div>
    </div>
  );
}
